import React from 'react';

import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Button, Tooltip, Typography, Flex } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faComment, faHeart } from '@fortawesome/pro-light-svg-icons';
import { faComment as faCommentPlain, faHeart as faHeartPlain } from '@fortawesome/pro-solid-svg-icons';

import AddButton from 'components/action/AddButton';
import OptionEditionModal from 'components/Modal/OptionEditionModal';

import {
	useCreateOptionMutation,
	useDeleteOptionMutation,
	useUpdateOptionMutation
} from '../../../graphql/generated';

import './Options.sass';
import NavigationButton from 'components/action/NavigationButton';
import Table from 'components/Table';
import ConfirmModal from 'components/Modal/ConfirmModal';

export const Options = (props: any) => {
	const [editedItem, setEditedItem] = React.useState<any | null>(null);

	const params: any = useParams();
	const [createOption] = useCreateOptionMutation();
	const [deleteData] = useDeleteOptionMutation();
	const [updateData] = useUpdateOptionMutation();
	const { t } = useTranslation();

	const { options = [] } = props;

	// Create option
	const handleCreateOption = async (values: Object) => {

		await createOption({
			variables: {
				...values,
				projectId: params.id,
			},
		});

		await props.refetch();
	};

	// Update
	const handleUpdateOption = async (id: string, values: Object) => {
		await updateData({
			variables: {
				id: id,
				...values,
			},
		});

		await props.refetch();
	};

	// Set gut decision
	const handleSetGutDecision = async (id: string, value: Boolean) => {
		props.options.forEach((o: any) => {
			if (o.id != id && o.isGutDecision) {
				handleUpdateOption(o.id, { isGutDecision: false });
			}
		});

		const result = await handleUpdateOption(id, { isGutDecision: !!value });

		await props.refetch();
	};

	// Delete option
	const handleDelete = async (id?: string) => {

		props.pushModal(
			<ConfirmModal
				title={t('models.option.messages.delete_confirmation')}
				t={t}
				onConfirm={async () => {

					console.log('deleteData', id);

					if (id == null) {
						return;
					}

					await deleteData({
						variables: { id },
					});

					await props.refetch();
				}}
				message={t(
					'models.option.messages.delete_confirmation_explanation'
				)}
			></ConfirmModal>
		);
	};

	const columns = [
		{
			title: '',
			dataIndex: 'edit',
			key: 'edit',
			width: 40,
			render: (text, record) => (
				<Tooltip title={props.scenario ? null : 'Edit'}>
					<Button
						shape="circle"
						disabled={props.scenario ? true : false}
						style={{ opacity: props.scenario ? 0.2 : 1 }}
						onClick={
							props.disabled
								? () => { }
								: () => {
									setEditedItem(record);
								}
						}
					>
						<FontAwesomeIcon
							icon={faEdit}
							style={{
								cursor: props.disabled
									? 'not-allowed'
									: 'initial',
								opacity: props.disabled ? 0.5 : 1
							}}
						/>
					</Button>
				</Tooltip>
			)
		},
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			className: 'w-100',
			sortable: true,
			defaultSortOrder: 'ascend',
			ellipsis: true,
			render: (text, record) => {
				return (
					<div className="name">
						{record.name}
						<div className="description">
							{record.description}
						</div>
					</div>
				);
			}
		},
		{
			title: t('models.option.fields.comments_short'),
			dataIndex: 'gut',
			key: 'gut',
			width: 100,
			className: 'text-center',
			render: (text, record) => {
				return (
					<Tooltip placement="left" title={record.comments || t("models.option.messages.no_comments")}>
						<Button
							shape="circle"
							className={record.comments ? 'active comments' : 'comments'}
						>
							<FontAwesomeIcon icon={record.comments ? faCommentPlain : faComment} />
						</Button>
					</Tooltip>
				);
			}
		},
		{
			title: t('models.option.fields.gut_short'),
			dataIndex: 'gut',
			key: 'gut',
			width: 50,
			render: (text, record) => {
				return (
					<Tooltip placement="left" title="Gut decision">
						<Button
							shape="circle"
							className={record.isGutDecision ? 'gut active' : 'gut'}
							onClick={() => {
								handleSetGutDecision(record.id, !record.isGutDecision);
							}}
						>
							<FontAwesomeIcon icon={record.isGutDecision ? faHeartPlain : faHeart} />
						</Button>
					</Tooltip>
				);
			}
		}
	];

	return (
		<div className="Options">
			<Typography.Title level={2}>{t('pages.options.title')}</Typography.Title>
			{false && <Typography.Title level={3}>{t('pages.options.subtitle')}</Typography.Title>}

			<div className="mb-3 mt-4 w-100" style={{ maxWidth: '1200px' }}>
				<Table
					pagination={false}
					dataSource={options}
					columns={columns}
					size="small"
				/>
			</div>

			<Flex justify="center" align="center" className="w-100">
				<AddButton className="mr-2" onClick={() => setEditedItem({})}></AddButton>
				<NavigationButton onClick={() => props.onGoNext('/projects/' + props?.id + '/options-evaluation')} className="ml-4">
					Next step
				</NavigationButton>
			</Flex>

			{editedItem && (
				<OptionEditionModal
					onHide={() => {
						setEditedItem(null);
					}}
					onDelete={() => {
						handleDelete(editedItem.id);
						setEditedItem(null);
					}}
					onSave={(values: Object) => {

						editedItem.id === undefined ? handleCreateOption({
							name: values.name,
							description: values.description,
							evaluation: values.evaluation,
							comments: values.comments,
						}) : handleUpdateOption(editedItem.id, values);
						setEditedItem(null);
					}}
					id={editedItem?.id}
					values={{
						name: editedItem?.name,
						description: editedItem?.description,
						comments: editedItem?.comments,
						evaluation: editedItem?.evaluation,
					}}
				></OptionEditionModal>
			)}
		</div>
	);
};
